import { useState, useEffect } from 'react';
import { BarChart3, Users, Eye, TrendingUp, Music, Play, Bell, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [notifyForm, setNotifyForm] = useState({ title: '', body: '', url: '' });
  const [sendingNotification, setSendingNotification] = useState(false);

  useEffect(() => {
    fetchAnalytics();
    fetchSubscriberCount();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/analytics/summary`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriberCount = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/push/subscribers`);
      setSubscriberCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch subscriber count:', error);
    }
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();
    if (!notifyForm.title || !notifyForm.body) {
      toast.error('Please fill in title and message');
      return;
    }
    
    setSendingNotification(true);
    try {
      const formData = new FormData();
      formData.append('title', notifyForm.title);
      formData.append('body', notifyForm.body);
      if (notifyForm.url) formData.append('url', notifyForm.url);
      
      const response = await axios.post(`${API_URL}/admin/push/send`, formData);
      toast.success(`🔔 Notification sent to ${response.data.sent} subscribers!`);
      setShowNotifyModal(false);
      setNotifyForm({ title: '', body: '', url: '' });
    } catch (error) {
      toast.error('Failed to send notification');
    } finally {
      setSendingNotification(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-zinc-400">Loading analytics...</div>
      </div>
    );
  }

  const totals = analytics?.totals || {};
  const today = analytics?.today || {};
  const week = analytics?.week || {};
  const topPages = analytics?.top_pages || [];
  const topMixes = analytics?.top_mixes || [];
  const dailyStats = analytics?.daily_stats || [];

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-3xl font-bold">Analytics Dashboard</h1>
        <div className="flex items-center gap-3">
          <Button 
            onClick={() => setShowNotifyModal(true)} 
            className="bg-[#D9F99D] text-black hover:bg-[#BEF264]"
          >
            <Bell className="w-4 h-4 mr-2" />
            Send Notification ({subscriberCount})
          </Button>
          <Button onClick={fetchAnalytics} variant="outline" className="bg-white/5 border-white/10">
            Refresh
          </Button>
        </div>
      </div>

      {/* Notification Modal */}
      <Dialog open={showNotifyModal} onOpenChange={setShowNotifyModal}>
        <DialogContent className="bg-[#18181B] border-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#D9F99D]" />
              Send Push Notification
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSendNotification} className="space-y-4">
            <p className="text-sm text-zinc-400">
              Send a notification to {subscriberCount} subscriber{subscriberCount !== 1 ? 's' : ''}
            </p>
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={notifyForm.title}
                onChange={(e) => setNotifyForm({ ...notifyForm, title: e.target.value })}
                placeholder="🎵 New Mix Alert!"
                className="bg-black/20 border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Message *</Label>
              <Textarea
                value={notifyForm.body}
                onChange={(e) => setNotifyForm({ ...notifyForm, body: e.target.value })}
                placeholder="DJ Shadow just dropped a new fire mix!"
                className="bg-black/20 border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Link (optional)</Label>
              <Input
                value={notifyForm.url}
                onChange={(e) => setNotifyForm({ ...notifyForm, url: e.target.value })}
                placeholder="/mixes"
                className="bg-black/20 border-white/10"
              />
            </div>
            <Button 
              type="submit" 
              disabled={sendingNotification}
              className="w-full bg-[#D9F99D] text-black hover:bg-[#BEF264]"
            >
              {sendingNotification ? 'Sending...' : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send to {subscriberCount} Subscribers
                </>
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Eye} label="Total Views" value={totals.pageviews || 0} color="#D9F99D" />
        <StatCard icon={Play} label="Total Plays" value={totals.plays || 0} color="#F472B6" />
        <StatCard icon={Users} label="Total Fans" value={totals.fans || 0} color="#8B5CF6" />
        <StatCard icon={Music} label="Total Mixes" value={totals.mixes || 0} color="#22D3EE" />
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="glass-card rounded-2xl p-6">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#D9F99D]" />
            Today
          </h3>
          <div className="space-y-3">
            <StatRow label="Page Views" value={today.views || 0} />
            <StatRow label="Mix Plays" value={today.plays || 0} />
            <StatRow label="Unique Visitors" value={today.unique_visitors || 0} />
          </div>
        </div>

        <div className="glass-card rounded-2xl p-6">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-[#8B5CF6]" />
            This Week
          </h3>
          <div className="space-y-3">
            <StatRow label="Unique Visitors" value={week.unique_visitors || 0} />
          </div>
        </div>

        <div className="glass-card rounded-2xl p-6">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#F472B6]" />
            Top Pages
          </h3>
          <div className="space-y-2">
            {topPages.length > 0 ? topPages.slice(0, 5).map((page, i) => (
              <div key={i} className="flex justify-between items-center text-sm">
                <span className="text-zinc-400 truncate">{page.page || '/'}</span>
                <span className="font-medium">{page.views}</span>
              </div>
            )) : <p className="text-zinc-500 text-sm">No data yet</p>}
          </div>
        </div>
      </div>

      <div className="glass-card rounded-2xl p-6">
        <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Music className="w-5 h-5 text-[#D9F99D]" />
          Top Mixes by Plays
        </h3>
        {topMixes.length > 0 ? (
          <div className="space-y-3">
            {topMixes.map((mix, index) => (
              <div key={mix.id} className="flex items-center gap-4 p-3 rounded-xl bg-white/5">
                <span className="text-2xl font-bold text-zinc-500 w-8">#{index + 1}</span>
                {mix.cover_url && (
                  <img src={mix.cover_url} alt={mix.title} className="w-12 h-12 rounded-lg object-cover" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{mix.title}</p>
                </div>
                <div className="flex items-center gap-2 text-[#D9F99D]">
                  <Play className="w-4 h-4" />
                  <span className="font-bold">{mix.plays_count || 0}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-zinc-500">No plays recorded yet</p>
        )}
      </div>

      {dailyStats.length > 0 && (
        <div className="glass-card rounded-2xl p-6">
          <h3 className="text-xl font-bold mb-4">Last 7 Days</h3>
          <DailyChart data={dailyStats} />
        </div>
      )}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="glass-card rounded-2xl p-6">
    <div className="flex items-center gap-3 mb-2">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <span className="text-zinc-400 text-sm">{label}</span>
    </div>
    <p className="text-3xl font-bold">{value.toLocaleString()}</p>
  </div>
);

const StatRow = ({ label, value }) => (
  <div className="flex justify-between items-center">
    <span className="text-zinc-400">{label}</span>
    <span className="font-bold text-lg">{value}</span>
  </div>
);

const DailyChart = ({ data }) => {
  const maxViews = Math.max(...data.map(d => d.total_views || 0), 1);
  
  return (
    <div className="flex items-end gap-2 h-40">
      {data.map((day, index) => {
        const height = ((day.total_views || 0) / maxViews) * 100;
        const dayName = new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' });
        return (
          <div key={index} className="flex-1 flex flex-col items-center gap-2">
            <div 
              className="w-full bg-gradient-to-t from-[#D9F99D] to-[#84CC16] rounded-t-lg"
              style={{ height: `${Math.max(height, 5)}%` }}
            />
            <span className="text-xs text-zinc-500">{dayName}</span>
          </div>
        );
      })}
    </div>
  );
};

export default AdminAnalytics;
